import { useEffect, useRef, type FormEvent } from 'react';
import { FieldSize, Size } from '@bms2/shared';
import { FieldShell } from './FieldShell';
import styles from './field.module.css';

/**
 * Textarea (piece 3): the native multi-line input with the field family's
 * edge, focus and error treatment. It auto-grows to a cap and then scrolls,
 * and keeps the browser's spell check on.
 */
export interface TextareaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  placeholder?: string;
  error?: string | null;
  disabled?: boolean;
  name?: string;
  rows?: number;
  /** The label read by assistive technology and not drawn, for a box inside a table cell. */
  labelHidden?: boolean;
  /** Which of the widths the box takes, Column in a line table's cell; a paragraph is Large, and Large is what a caller naming none gets (decisions 0583, 0647, 1322). */
  size?: FieldSize;
}

export const textareaDocumentedStates = ['default', 'error', 'disabled'] as const;

function grow(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

export function Textarea({ label, value, onChange, onBlur, required, placeholder, error, disabled, name, rows = 2, labelHidden = false, size = Size.Large }: TextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (ref.current) grow(ref.current);
  }, [value]);
  const onInput = (e: FormEvent<HTMLTextAreaElement>) => grow(e.currentTarget);
  return (
    <FieldShell label={label} required={required ?? false} error={error ?? null} disabled={disabled ?? false} labelHidden={labelHidden} size={size}>
      {({ inputId, messageId }) => (
        <textarea
          ref={ref}
          id={inputId}
          className={`${styles.input} ${styles.textarea} ${styles.withIcon}`}
          name={name}
          rows={rows}
          value={value}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? messageId : undefined}
          onChange={(e) => onChange(e.target.value)}
          onInput={onInput}
          onBlur={onBlur}
        />
      )}
    </FieldShell>
  );
}
